import { useEffect, useMemo, useState } from "react";
import { Mail, Send, X, Loader2, CheckCircle2, AlertTriangle, Info } from "lucide-react";
import api from "@/lib/api";

const MERGE_TOKENS = [
  { key: "first_name",            label: "First name" },
  { key: "full_name",             label: "Full name" },
  { key: "property_address",      label: "Property address" },
  { key: "suburb",                label: "Suburb" },
  { key: "last_test_name",        label: "Last test" },
  { key: "last_test_date",        label: "Last test date" },
  { key: "partner_business_name", label: "Your business name" },
];

const EXCLUSION_LABEL = {
  no_valid_email:               "no valid email address",
  unsubscribed_or_bounced:      "unsubscribed / bounced",
  not_attributed_to_partner:    "no longer attributed to you",
};

/**
 * Bulk email campaign modal.
 *
 * Props
 *   customerKeys — array of `e:foo@bar` / `p:0400000` keys currently selected
 *   asPartnerId  — optional admin impersonation partner_id (passed as ?as_partner_id=)
 *   onClose      — called after Send success or Cancel
 */
export default function BulkEmailModal({ customerKeys, asPartnerId, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState(null);           // eligibility + rate_limit + rendered
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);

  const paramSuffix = asPartnerId ? `?as_partner_id=${encodeURIComponent(asPartnerId)}` : "";

  // Preview + eligibility whenever selection or draft changes.
  useEffect(() => {
    if (!customerKeys?.length) return;
    let cancelled = false;
    setLoadingPreview(true);
    (async () => {
      try {
        const r = await api.post(`/partner/campaigns/preview${paramSuffix}`, {
          customer_keys: customerKeys,
          subject, body,
          preview_key: customerKeys[0],
        });
        if (!cancelled) setPreview(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to preview");
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerKeys, subject, body]);

  const insertToken = (key) => {
    const el = document.getElementById("bulk-email-body-textarea");
    const token = `{{${key}}}`;
    if (!el) { setBody((b) => (b || "") + token); return; }
    const start = el.selectionStart ?? body.length;
    const end   = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + token + body.slice(end));
    // Restore focus + cursor position after React re-renders.
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  };

  const sendTest = async () => {
    if (!subject.trim() || !body.trim()) {
      setError("Add a subject and body before sending a test.");
      return;
    }
    setTestSending(true); setTestResult(null); setError("");
    try {
      const r = await api.post(`/partner/campaigns/send-test${paramSuffix}`, {
        subject, body,
      });
      setTestResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Test send failed");
    } finally {
      setTestSending(false);
    }
  };

  const sendCampaign = async () => {
    setSending(true); setError("");
    try {
      const r = await api.post(`/partner/campaigns/send${paramSuffix}`, {
        customer_keys: customerKeys,
        subject, body,
      });
      setResult(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Send failed");
    } finally {
      setSending(false);
      setConfirming(false);
    }
  };

  const rateBlocked = (preview?.rate_limit?.remaining ?? 0) <= 0;
  const eligibleCount = preview?.eligible_count || 0;
  const excludedCount = preview?.excluded_count || 0;
  const selectedCount = preview?.selected_count || customerKeys.length;
  const canSend = eligibleCount > 0 && !!subject.trim() && !!body.trim() && !rateBlocked;

  const iframeSrcDoc = useMemo(() => preview?.preview?.html || "", [preview]);

  // ── Post-send screen ──────────────────────────────────────────────
  if (result) {
    return (
      <ModalShell title="Campaign sent" onClose={onClose}>
        <div className="p-6 space-y-4" data-testid="bulk-email-result">
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 className="w-5 h-5" />
            <div className="font-semibold">Campaign sent</div>
          </div>
          <ul className="text-sm text-slate-700 space-y-1">
            <li>Selected: <strong>{result.selected_count}</strong></li>
            <li>Eligible: <strong>{result.eligible_count}</strong></li>
            <li>Excluded: <strong>{result.excluded_count}</strong></li>
            <li className="text-emerald-700">Sent: <strong>{result.sent_count}</strong></li>
            {result.failed_count > 0 && (
              <li className="text-red-700">Failed: <strong>{result.failed_count}</strong></li>
            )}
          </ul>
          <div className="pt-2 flex justify-end">
            <button
              onClick={onClose}
              className="rounded-xl bg-slate-900 text-white text-sm font-semibold px-4 py-2 hover:bg-slate-800"
              data-testid="bulk-email-close-result"
            >Done</button>
          </div>
        </div>
      </ModalShell>
    );
  }

  // ── Confirmation screen ────────────────────────────────────────────
  if (confirming) {
    return (
      <ModalShell title="Ready to send?" onClose={() => setConfirming(false)}>
        <div className="p-6 space-y-5" data-testid="bulk-email-confirm">
          <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 p-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              You&apos;re about to broadcast <strong>{eligibleCount}</strong> individual email{eligibleCount === 1 ? "" : "s"}. Each recipient gets a private send — nobody sees the others.
            </div>
          </div>
          <ul className="text-sm text-slate-700 space-y-1">
            <li>Selected: <strong>{selectedCount}</strong></li>
            <li>Eligible: <strong>{eligibleCount}</strong></li>
            <li>Excluded: <strong>{excludedCount}</strong></li>
            <li>About to be sent: <strong className="text-emerald-700">{eligibleCount}</strong></li>
          </ul>
          <div className="text-xs text-slate-500">
            Subject: <span className="font-mono">{subject}</span>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setConfirming(false)}
              disabled={sending}
              className="rounded-xl bg-slate-100 text-slate-700 text-sm font-semibold px-4 py-2 hover:bg-slate-200"
            >Back</button>
            <button
              onClick={sendCampaign}
              disabled={sending}
              className="rounded-xl bg-emerald-600 text-white text-sm font-semibold px-4 py-2 hover:bg-emerald-700 inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="bulk-email-confirm-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send {eligibleCount} email{eligibleCount === 1 ? "" : "s"}
            </button>
          </div>
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">{error}</div>
          )}
        </div>
      </ModalShell>
    );
  }

  // ── Compose screen ────────────────────────────────────────────────
  return (
    <ModalShell title={`Email ${customerKeys.length} customer${customerKeys.length === 1 ? "" : "s"}`} onClose={onClose}>
      <div className="p-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6" data-testid="bulk-email-compose">
        {/* LEFT — compose */}
        <div className="space-y-4 min-w-0">
          {/* Eligibility summary */}
          {loadingPreview ? (
            <div className="text-xs text-slate-500 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Checking eligibility…
            </div>
          ) : preview && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs space-y-1" data-testid="bulk-email-eligibility">
              <div className="flex items-center gap-2 text-slate-700">
                <span className="font-semibold">{eligibleCount}</span> eligible ·
                <span className="text-red-700">{excludedCount}</span> excluded ·
                <span className="text-slate-500">{selectedCount} selected</span>
              </div>
              {excludedCount > 0 && (
                <details className="text-slate-500">
                  <summary className="cursor-pointer">Why excluded?</summary>
                  <ul className="mt-1 ml-4 list-disc space-y-0.5">
                    {preview.excluded.map((e, i) => (
                      <li key={i}>
                        {e.name || e.email || e.customer_key} — {EXCLUSION_LABEL[e.reason] || e.reason}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {rateBlocked && (
                <div className="text-red-700 mt-1">
                  Rate limit reached: {preview.rate_limit.used_last_24h}/{preview.rate_limit.max_campaigns_per_24h} campaigns in the last 24h.
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Subject</label>
            <input
              data-testid="bulk-email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Time for your annual water test check-up"
              className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs uppercase tracking-wider text-slate-500">Body</label>
              <div className="text-[10px] text-slate-500">Merge tokens →</div>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {MERGE_TOKENS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => insertToken(t.key)}
                  data-testid={`bulk-email-token-${t.key}`}
                  className="text-[10px] font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200"
                >{"{{"}{t.key}{"}}"}</button>
              ))}
            </div>
            <textarea
              id="bulk-email-body-textarea"
              data-testid="bulk-email-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={"Hi {{first_name}},\n\nIt has been a while since your {{last_test_name}} on {{last_test_date}}. Would you like to book a refresh?\n\nCheers,\n{{partner_business_name}}"}
              rows={10}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/40 font-mono resize-y"
            />
          </div>

          <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800 flex items-start gap-2">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              Each customer receives a private email with an auto-appended
              unsubscribe link. Your business name + contact details are
              added as a signature.
            </div>
          </div>

          <div className="flex flex-wrap justify-between items-center gap-2 pt-2">
            <button
              onClick={sendTest}
              disabled={testSending || !subject.trim() || !body.trim()}
              data-testid="bulk-email-send-test"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-100 text-slate-700 text-sm font-semibold px-3 py-2 hover:bg-slate-200 disabled:opacity-50"
            >
              {testSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Send test to me
            </button>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="rounded-xl bg-white border border-slate-300 text-slate-700 text-sm font-semibold px-4 py-2 hover:bg-slate-50"
              >Cancel</button>
              <button
                onClick={() => setConfirming(true)}
                disabled={!canSend}
                data-testid="bulk-email-continue"
                className="rounded-xl bg-emerald-600 text-white text-sm font-semibold px-4 py-2 hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                Continue → {eligibleCount}
              </button>
            </div>
          </div>

          {testResult && (
            <div className="text-xs rounded-xl border p-2 bg-emerald-50 border-emerald-200 text-emerald-800" data-testid="bulk-email-test-result">
              {testResult.sent ? `✓ Test sent to ${testResult.to}` : `✗ Test failed: ${testResult.error || "unknown"}`}
            </div>
          )}

          {error && !confirming && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">{error}</div>
          )}
        </div>

        {/* RIGHT — preview */}
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
            Preview for {preview?.eligible?.[0]?.name || "first recipient"}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden h-[520px]">
            {iframeSrcDoc ? (
              <iframe
                title="preview"
                srcDoc={iframeSrcDoc}
                sandbox=""
                className="w-full h-full"
                data-testid="bulk-email-preview-iframe"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                Compose a subject + body to see the preview.
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalShell>
  );
}


function ModalShell({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div className="text-slate-900 font-semibold text-sm">{title}</div>
          <button onClick={onClose} className="rounded-full w-8 h-8 flex items-center justify-center hover:bg-slate-100" data-testid="bulk-email-modal-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
